import { useEffect, type ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { FullPageLoader } from '@/components/loading-state'
import { useStartupStore } from '@/features/auth/startup/startup-store'
import {
  selectIsAdmin,
  useSessionStore,
} from '@/lib/auth/session-store'
import { useUiStore } from '@/lib/ui/ui-store'

/**
 * Global client guard (D-043). Server authorization remains authoritative;
 * this only avoids rendering protected screens for a logged-out visitor,
 * preserves the attempted deep link through login, and keeps the pending
 * activity link across the auth flow (UI_PARITY §0.10).
 */
export function RequireAuth() {
  const status = useSessionStore((state) => state.status)
  const accessToken = useSessionStore((state) => state.accessToken)
  const location = useLocation()
  const setPendingActivityPath = useUiStore(
    (state) => state.setPendingActivityPath,
  )

  const signedOut = status !== 'authenticated' || !accessToken
  const attemptedPath = `${location.pathname}${location.search}`

  useEffect(() => {
    if (signedOut && location.pathname.startsWith('/activity')) {
      setPendingActivityPath(attemptedPath)
    }
  }, [
    attemptedPath,
    location.pathname,
    setPendingActivityPath,
    signedOut,
  ])

  if (status === 'unknown') return <FullPageLoader />

  if (signedOut) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: attemptedPath }}
      />
    )
  }

  return <Outlet />
}

export function RequireAdmin({ children }: { children: ReactNode }) {
  const isAdmin = useSessionStore(selectIsAdmin)
  const user = useSessionStore((state) => state.user)
  const startupPhase = useStartupStore((state) => state.phase)
  // A cold deep link authenticates before `/me` has populated `isAdmin`.
  // Wait for the startup profile fetch only while the flag is still unknown;
  // an explicit `isAdmin: false` can redirect immediately.
  if (user?.isAdmin === undefined && startupPhase === 'running') {
    return <FullPageLoader />
  }
  if (!isAdmin) return <Navigate to="/home" replace />
  return children
}
